import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { getUserTaskHistory, getDailyProfit } from '../services/taskService';
import { doc, onSnapshot, query, collection, where, getDocs, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

function RecordsPage() {
  const { user } = useAuth();
  const { userProfile, refreshProfile } = useUserProfile(user?.uid);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyProfit, setDailyProfit] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [hasKnifeApplied, setHasKnifeApplied] = useState(false);
  const [knifeRemovedPendingSubmit, setKnifeRemovedPendingSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Get today's date key (YYYY-MM-DD format)
  const getTodayKey = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (!user?.uid) return;

    const loadRecords = async () => {
      try {
        setLoading(true);
        const todayKey = getTodayKey();

        // Fetch ALL task submissions (not just today's) to show pending tasks
        const q = query(
          collection(db, 'taskSubmissions'),
          where('userId', '==', user.uid)
        );

        const querySnapshot = await getDocs(q);
        const allTasks = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allTasks.push({
            id: doc.id,
            ...data
          });
        });

        // Sort by creation time (newest first)
        allTasks.sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(0);
          return timeB - timeA;
        });

        // Separate pending and completed tasks
        // Only show knife-related tasks that are still pending or ready_for_optimization
        // Completed knife tasks should disappear (not shown)
        const pendingTasks = allTasks.filter(task => 
          (task.status === 'pending' || task.status === 'ready_for_optimization') &&
          (task.knifeApplied || task.knifeAmount) // Only show knife-related tasks
        );
        // Only show normal (non-knife) completed tasks
        const completedTasks = allTasks.filter(task => 
          task.status === 'completed' && 
          task.date === todayKey &&
          !task.knifeApplied && 
          !task.knifeAmount
        );

        // Show pending tasks first, then today's completed tasks
        const displayTasks = [...pendingTasks, ...completedTasks];
        setTasks(displayTasks);

        // Count only completed tasks for today
        setTasksCount(completedTasks.length);

        // Calculate total profit for today (only from completed tasks)
        const totalProfit = completedTasks.reduce((sum, task) => {
          return sum + (parseFloat(task.commission) || 0);
        }, 0);
        setDailyProfit(totalProfit);

        // Also try to get from dailyProfits collection
        try {
          const profitData = await getDailyProfit(user.uid, todayKey);
          if (profitData.totalProfit > 0) {
            setDailyProfit(profitData.totalProfit);
            setTasksCount(profitData.tasksCompleted || completedTasks.length);
          }
        } catch (error) {
          console.error('Error loading daily profit:', error);
        }

        // Check if knife was removed and update pending tasks
        // Get user profile to check knife status
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const knifeRemoved = !userData.hasNegativeBalance && !userData.isKnifeApplied && userData.knifeRemovedPendingSubmit;
            
            if (knifeRemoved) {
              const tasksQuery = query(
                collection(db, 'taskSubmissions'),
                where('userId', '==', user.uid),
                where('status', '==', 'pending')
              );
              const tasksSnapshot = await getDocs(tasksQuery);
              
              const updatePromises = [];
              tasksSnapshot.forEach((taskDoc) => {
                const taskData = taskDoc.data();
                // Only update tasks that have knifeApplied flag or knifeAmount
                if (taskData.knifeApplied || taskData.knifeAmount) {
                  updatePromises.push(
                    updateDoc(doc(db, 'taskSubmissions', taskDoc.id), {
                      status: 'ready_for_optimization',
                      readyAt: Timestamp.now(),
                      updatedAt: Timestamp.now()
                    })
                  );
                }
              });
              
              if (updatePromises.length > 0) {
                await Promise.all(updatePromises);
                console.log(`✅ Updated ${updatePromises.length} pending tasks to ready_for_optimization on load`);
                // The real-time listener will automatically update the UI
              }
            }
          }
        } catch (error) {
          console.error('Error checking/updating task statuses on load:', error);
        }
      } catch (error) {
        console.error('Error loading records:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecords();

    // Set up real-time listener for all tasks
    if (user?.uid) {
      const q = query(
        collection(db, 'taskSubmissions'),
        where('userId', '==', user.uid)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const allTasks = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allTasks.push({
            id: doc.id,
            ...data
          });
        });

        // Sort by creation time (newest first)
        allTasks.sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(0);
          return timeB - timeA;
        });

        const todayKey = getTodayKey();
        
        // Separate pending and completed tasks
        // Only show tasks that are still pending or ready_for_optimization
        // Completed tasks from knife submissions should not appear (they disappear after submit)
        const pendingTasks = allTasks.filter(task => 
          (task.status === 'pending' || task.status === 'ready_for_optimization') &&
          (task.knifeApplied || task.knifeAmount) // Only show knife-related tasks in pending
        );
        // Only show completed tasks that are NOT from knife submissions (normal tasks)
        const completedTasks = allTasks.filter(task => 
          task.status === 'completed' && 
          task.date === todayKey &&
          !task.knifeApplied && 
          !task.knifeAmount
        );

        // Show pending tasks first, then today's completed tasks
        const displayTasks = [...pendingTasks, ...completedTasks];
        setTasks(displayTasks);

        // Count only completed tasks for today
        setTasksCount(completedTasks.length);

        // Calculate total profit for today (only from completed tasks)
        const totalProfit = completedTasks.reduce((sum, task) => {
          return sum + (parseFloat(task.commission) || 0);
        }, 0);
        setDailyProfit(totalProfit);
      }, (error) => {
        console.error('Error in real-time listener:', error);
      });

      return () => unsubscribe();
    }
  }, [user?.uid]);

  // Listen to user profile for knife status and update task statuses
  useEffect(() => {
    if (!user?.uid) return;
    
    const userRef = doc(db, 'users', user.uid);
    let previousKnifeStatus = hasKnifeApplied;
    let previousPendingSubmit = knifeRemovedPendingSubmit;
    
    const unsubscribe = onSnapshot(userRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = { id: docSnapshot.id, ...docSnapshot.data() };
        const knifeStatus = userData.hasNegativeBalance || userData.isKnifeApplied || false;
        const pendingSubmit = userData.knifeRemovedPendingSubmit || false;
        
        // Update all pending tasks to "ready_for_optimization" when:
        // 1. Knife is removed (knifeStatus is false) AND
        // 2. knifeRemovedPendingSubmit becomes true (just changed from false to true)
        const knifeJustRemoved = !knifeStatus && pendingSubmit && !previousPendingSubmit;
        const knifeWasRemoved = !knifeStatus && pendingSubmit;
        
        if (knifeJustRemoved || (knifeWasRemoved && previousKnifeStatus)) {
          try {
            const tasksQuery = query(
              collection(db, 'taskSubmissions'),
              where('userId', '==', user.uid),
              where('status', '==', 'pending')
            );
            const tasksSnapshot = await getDocs(tasksQuery);
            
            const updatePromises = [];
            tasksSnapshot.forEach((taskDoc) => {
              const taskData = taskDoc.data();
              // Only update tasks that have knifeApplied flag or knifeAmount
              if (taskData.knifeApplied || taskData.knifeAmount) {
                updatePromises.push(
                  updateDoc(doc(db, 'taskSubmissions', taskDoc.id), {
                    status: 'ready_for_optimization',
                    readyAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                  })
                );
              }
            });
            
            if (updatePromises.length > 0) {
              await Promise.all(updatePromises);
              console.log(`✅ Updated ${updatePromises.length} tasks to ready_for_optimization`);
            }
          } catch (error) {
            console.error('Error updating task statuses:', error);
          }
        }
        
        // Update state after processing
        previousKnifeStatus = knifeStatus;
        previousPendingSubmit = pendingSubmit;
        setHasKnifeApplied(knifeStatus);
        setKnifeRemovedPendingSubmit(pendingSubmit);
      }
    }, (error) => {
      console.error('Error in user profile listener:', error);
    });
    
    return () => unsubscribe();
  }, [user?.uid]);

  // Handle submit after knife removal - updates all ready tasks
  const handleSubmitAfterKnifeRemoval = async (taskId = null) => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      
      // Update user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        knifeRemovedPendingSubmit: false,
        updatedAt: Timestamp.now()
      });
      
      // Update all tasks with status 'ready_for_optimization' to 'completed'
      // If taskId is provided, update only that task; otherwise update all ready tasks
      // Firestore doesn't support 'in' with multiple values, so we query for ready_for_optimization
      const tasksQuery = query(
        collection(db, 'taskSubmissions'),
        where('userId', '==', user.uid),
        where('status', '==', 'ready_for_optimization')
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const updatePromises = [];
      
      tasksSnapshot.forEach((taskDoc) => {
        const taskData = taskDoc.data();
        // If taskId is provided, only update that specific task
        // Otherwise, update all ready_for_optimization tasks
        if (taskId) {
          if (taskDoc.id === taskId) {
            updatePromises.push(
              updateDoc(doc(db, 'taskSubmissions', taskDoc.id), {
                status: 'completed',
                completedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
              })
            );
          }
        } else {
          // Update all ready_for_optimization tasks
          if (taskData.status === 'ready_for_optimization') {
            updatePromises.push(
              updateDoc(doc(db, 'taskSubmissions', taskDoc.id), {
                status: 'completed',
                completedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
              })
            );
          }
        }
      });
      
      await Promise.all(updatePromises);
      
      // Refresh profile
      refreshProfile();
      
      // Show success message
      const taskCount = updatePromises.length;
      alert(`✅ Optimization tasks enabled! ${taskCount} task(s) moved to completed history.`);
      
      setSubmitting(false);
    } catch (error) {
      console.error('Error submitting after knife removal:', error);
      alert('Error enabling optimization. Please try again.');
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return dateObj.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDateFull = (timestamp) => {
    if (!timestamp) return 'N/A';
    const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Task ID copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy task ID');
    });
  };

  const getVIPBadgeColor = (vipLevel) => {
    const colors = {
      'VIP1': { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: 'white' },
      'VIP2': { bg: 'linear-gradient(135deg, #10b981, #059669)', text: 'white' },
      'VIP3': { bg: 'linear-gradient(135deg, #a855f7, #7c3aed)', text: 'white' },
      'VIP4': { bg: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'white' }
    };
    return colors[vipLevel] || colors['VIP1'];
  };

  if (loading) {
    return (
      <div className="records-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="records-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        {/* Header Section */}
        <div className="records-header mb-3 mb-md-4">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
            <div className="mb-2 mb-sm-0">
              <h2 className="records-page-title mb-1">
                <i className="bi bi-journal-text me-2"></i>
                Today's Records
              </h2>
              <p className="text-muted mb-0 small records-date-text">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row g-2 g-md-3 mb-3 mb-md-4">
            <div className="col-6 col-md-6">
              <div className="record-summary-card">
                <div className="record-summary-icon bg-primary">
                  <i className="bi bi-check-circle"></i>
                </div>
                <div className="record-summary-content">
                  <div className="record-summary-value">{tasksCount}</div>
                  <div className="record-summary-label">Tasks Completed</div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-6">
              <div className="record-summary-card">
                <div className="record-summary-icon bg-success">
                  <i className="bi bi-currency-dollar"></i>
                </div>
                <div className="record-summary-content">
                  <div className="record-summary-value">${dailyProfit.toFixed(2)}</div>
                  <div className="record-summary-label">Today's Profit</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Tasks Section */}
        {tasks.filter(task => task.status === 'pending' || task.status === 'ready_for_optimization').length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h5 className="mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
              <i className="bi bi-clock-history me-2" style={{ color: '#f59e0b' }}></i>
              Pending Tasks ({tasks.filter(task => task.status === 'pending' || task.status === 'ready_for_optimization').length})
            </h5>
            <div className="records-list">
              {tasks
                .filter(task => task.status === 'pending' || task.status === 'ready_for_optimization')
                .map((task) => {
                  return renderTaskCard(task);
                })}
            </div>
          </div>
        )}

        {/* Completed Tasks Section */}
        {tasks.filter(task => task.status === 'completed').length > 0 && (
          <div>
            <h5 className="mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
              <i className="bi bi-check-circle me-2" style={{ color: '#10b981' }}></i>
              Today's Completed Tasks ({tasks.filter(task => task.status === 'completed').length})
            </h5>
            <div className="records-list">
              {tasks
                .filter(task => task.status === 'completed')
                .map((task) => {
                  return renderTaskCard(task);
                })}
            </div>
          </div>
        )}

        {/* No Records Message */}
        {tasks.length === 0 && (
          <div className="records-placeholder">
            <div className="records-icon">
              <i className="bi bi-journal-text"></i>
            </div>
            <h2 className="records-title">No Records Yet</h2>
            <p className="records-subtitle">Completed tasks will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Helper function to render task card
  function renderTaskCard(task) {
    const hasMultipleProducts = task.products && task.products.length > 1;
    const products = hasMultipleProducts ? task.products : [{ 
      image: task.productImage, 
      title: task.productTitle, 
      price: task.totalAmount 
    }];
    const totalAmount = task.knifeAmount || task.totalAmount || 0;
    const vipBadgeStyle = getVIPBadgeColor(task.vipLevel || 'VIP1');
    
    return (
      <div key={task.id} className="professional-record-card">
        {/* Status Badge - Top Right */}
        <div className="record-status-badge">
          {task.status === 'pending' ? (
            <span className="status-badge status-pending">
              <i className="bi bi-clock me-1"></i>
              Pending
            </span>
          ) : task.status === 'ready_for_optimization' ? (
            <span className="status-badge" style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white'
            }}>
              <i className="bi bi-check-circle me-1"></i>
              Ready for Optimization
            </span>
          ) : (
            <span className="status-badge status-completed">
              <i className="bi bi-check-circle me-1"></i>
              Completed
            </span>
          )}
        </div>

        {/* Header Section */}
        <div className="record-header-section-compact">
          <div className="record-header-top-compact">
            <span className="products-badge-compact">
              {hasMultipleProducts ? `${products.length} Products` : '1 Product'}
            </span>
            <div 
              className="task-id-container-compact"
              onClick={() => copyToClipboard(task.taskCode)}
              style={{ cursor: 'pointer' }}
              title="Click to copy Task ID"
            >
              <i className="bi bi-tag me-1"></i>
              <span className="task-id-text-compact">{task.taskCode}</span>
              <i className="bi bi-clipboard ms-2" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>
            </div>
          </div>
        </div>

        {/* Products Display Section */}
        <div className="record-products-section">
          <div className="products-grid-compact">
            {products.map((product, idx) => (
              <div key={product.id || idx} className="product-card-item-compact">
                <div className="product-image-wrapper-compact">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="product-card-image-compact"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="product-image-placeholder-compact" style={{ display: 'none' }}>
                    <i className="bi bi-image"></i>
                  </div>
                </div>
                <div className="product-card-content-compact">
                  <h6 className="product-card-title-compact">{product.title}</h6>
                  <div className="product-card-price-compact">
                    {formatCurrency(product.price || 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Financial Information Section */}
        <div className="record-financial-section-compact">
          <div className="financial-row-compact">
            <div className="financial-item-compact">
              <span className="financial-label-compact">Amount:</span>
              <span className="financial-value-compact">{formatCurrency(totalAmount)}</span>
            </div>
            <div className="financial-item-compact">
              <span className="financial-label-compact">Commission:</span>
              <span className="financial-value-compact financial-commission-compact">
                +{formatCurrency(task.commission || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="record-metadata-section-compact">
          <div className="metadata-left-compact">
            <div className="metadata-item-compact">
              <span className="metadata-label-compact">Completed:</span>
              <span className="metadata-value-compact">{formatDate(task.createdAt)}</span>
            </div>
            <div className="metadata-date-compact">
              {formatDateFull(task.createdAt)}
            </div>
          </div>
          <div className="metadata-right-compact">
            <span 
              className="vip-badge-compact"
              style={{
                background: vipBadgeStyle.bg,
                color: vipBadgeStyle.text
              }}
            >
              {task.vipLevel || 'VIP1'}
            </span>
          </div>
        </div>

        {/* Submit Button - For ready_for_optimization tasks */}
        {(task.status === 'ready_for_optimization' || (task.status === 'pending' && knifeRemovedPendingSubmit && !hasKnifeApplied)) && (
          <div className="record-submit-section">
            <button
              onClick={() => handleSubmitAfterKnifeRemoval(task.id)}
              disabled={hasKnifeApplied || submitting || !knifeRemovedPendingSubmit}
              className="submit-button"
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Submit & Enable Optimization
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default RecordsPage;

